import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { AgentApiProfitsMonthlyTotals } from '@/types/serializers';

type Props = {
  data?: AgentApiProfitsMonthlyTotals;
  isLoading?: boolean;
};

const BAR_COLOR = '#005fff';

type ChartRow = {
  key: string;
  label: string;
  value: number;
};

function formatMonthLabel(year: number, month: number): string {
  // 過年度の 12 月のみ年プレフィックスで境界を示す（例: 2025年12月）。それ以外は「M月」。
  const currentYear = new Date().getFullYear();
  if (year < currentYear && month === 12) {
    return `${year}年${month}月`;
  }
  return `${month}月`;
}

function buildRows(data: AgentApiProfitsMonthlyTotals | undefined): ChartRow[] {
  if (!data) return [];
  return data.months.map((m) => ({
    key: `${m.year}-${String(m.month).padStart(2, '0')}`,
    label: formatMonthLabel(m.year, m.month),
    value: m.value,
  }));
}

function ChartSkeleton() {
  return (
    <div className="relative h-[333px] w-full">
      <div className="flex h-[303px] flex-col justify-between">
        {Array.from({ length: 6 }).map((_, index) => (
          <div key={index} className="flex items-center gap-[14px]">
            <div className="h-3 w-14 shrink-0 animate-skeleton rounded-full bg-border" />
            <div className="h-px flex-1 border-t border-dashed border-border" />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ProfitChart({ data, isLoading }: Props) {
  const rows = buildRows(data);
  const isEmpty = !isLoading && (data?.total_value ?? 0) === 0;

  return (
    <div className="w-full rounded bg-white p-xl shadow-layer1">
      <div className="mb-lg flex flex-col gap-1">
        <p className="text-sm font-bold text-sub">売上合計</p>
        <p className="text-2xl font-bold text-main">
          {isLoading || isEmpty ? '¥---' : data?.formatted_total_value}
        </p>
      </div>

      {isLoading ? (
        <ChartSkeleton />
      ) : (
        <div className="relative [&_*]:outline-hidden [&_*]:focus:outline-hidden [&_*]:focus-visible:outline-hidden">
          {isEmpty && (
            <p className="pointer-events-none absolute top-3 left-1/2 z-10 -translate-x-1/2 text-base font-bold text-sub">
              表示するデータがありません
            </p>
          )}
          <ResponsiveContainer width="100%" height={333}>
            <BarChart data={rows} margin={{ top: 24, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#e5e7eb" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 12, fill: '#6d6d6d' }}
                axisLine={false}
                tickLine={false}
                interval={0}
              />
              <YAxis
                tick={{ fontSize: 12, fill: '#6d6d6d' }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v: number) => v.toLocaleString()}
                allowDecimals={false}
                width={70}
              />
              <Tooltip
                cursor={{ fill: 'rgba(0, 95, 255, 0.05)' }}
                content={({ active, payload }) => {
                  if (!active || !payload || payload.length === 0) return null;
                  const value = Number(payload[0]?.value ?? 0);
                  return (
                    <div
                      role="tooltip"
                      aria-live="polite"
                      className="rounded bg-white px-2.5 py-1.5 text-xs font-bold text-[#262626] shadow-[0px_0px_3px_0px_rgba(0,0,0,0.2)]"
                    >
                      ¥{value.toLocaleString()}
                    </div>
                  );
                }}
              />
              <Bar dataKey="value" fill={BAR_COLOR} radius={[4, 4, 0, 0]} maxBarSize={50} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
